/**
 * Who may do what inside a DEAL document's editor.
 *
 * These rules were spread across the container as inline `actorRole === ...`
 * expressions on individual props, which is how the receiver ended up locked
 * into EDITING mode: each site read a slightly different signal, and one of
 * them read a value snapshotted before the document had loaded. One function,
 * derived from props, so the whole policy can be read — and tested — at once.
 *
 * The inputs:
 *  - `actorRole` — who the viewer is on THIS document (creator / receiver, or
 *    'lost' for a user removed from the deal whose comments still live on).
 *  - `hasBeenSharedWithCounterparty` — whether the document has a receiver team,
 *    i.e. it has been sent across the table at least once. This stays true after
 *    possession comes back, which is the point: once the other side has read a
 *    version, later changes have to be visible as changes.
 *  - `disableWordExport` — the template author's `disable_word_export` flag,
 *    inherited by every deal document created from that template.
 *
 * This is a UI-affordance layer, not an authorization boundary — the server
 * still enforces possession, permissions and suggestion gates on every mutation.
 */

export type DealActorRole = 'creator' | 'receiver' | 'lost';

export interface DealEditorCapabilityInput {
  actorRole: DealActorRole;
  hasBeenSharedWithCounterparty: boolean;
  /** Template's `disable_word_export`. Absent is treated as "export allowed". */
  disableWordExport?: boolean;
}

export interface DealEditorCapabilities {
  /**
   * Direct editing is off; every change must be a tracked suggestion, and the
   * mode dropdown is locked so it cannot be switched back.
   */
  mustSuggest: boolean;
  /** File → Save version. The owner's record of the document's history. */
  allowSaveVersion: boolean;
  /** File → Page setup (and the ruler's page-margin handles). */
  allowPageSetup: boolean;
  /** Insert → Watermark. A document-wide statement, like page geometry. */
  allowWatermark: boolean;
  /** Insert → Placeholder. Authoring a field the OTHER side is asked to fill. */
  allowInsertPlaceholder: boolean;
  /**
   * File → Open, i.e. "import a new version".
   *
   * This is NOT an import-as-suggestions path: it snapshots the current body
   * and then REPLACES it wholesale. Left in a counterparty's hands it is a way
   * to substitute the entire agreement while the document is in suggesting
   * mode, producing no tracked changes for the owner to review — the one thing
   * the suggesting lock exists to prevent. Owner-only until an
   * import-as-suggestions path exists.
   *
   * Also off when the template sets `disable_word_export`. Export and import
   * are one control in two directions: a template that refuses to hand the
   * document to Word cannot accept a Word file back over it, or the block is
   * decorative. It matters most on RESTRICTED templates, where a hand-edited
   * round trip rewrites locked wording — the `propdocs:restricted` SDTs survive
   * Word, so the document still looks correctly restricted afterwards and
   * nothing on import compares the locked content against the outgoing body.
   */
  allowImportNewVersion: boolean;
}

export const dealEditorCapabilities = ({
  actorRole,
  hasBeenSharedWithCounterparty,
  disableWordExport = false,
}: DealEditorCapabilityInput): DealEditorCapabilities => {
  // Anything that is not positively the creator is treated as the counterparty.
  // Testing for 'receiver' let the 'lost' role — which is what an absent or
  // unrecognised currentUserRole resolves to — through with full creator
  // capabilities, including replacing the whole document body.
  const isCounterparty = actorRole !== 'creator';

  return {
    // A counterparty NEVER edits directly. A creator loses direct editing the
    // moment the document has been shared — including while it is back in their
    // possession, because the counterparty has already read a version and any
    // silent rewrite of it is invisible to them.
    mustSuggest: isCounterparty || hasBeenSharedWithCounterparty,
    // Everything below is about OWNERSHIP of the document rather than its text,
    // so it turns on the actor alone: the counterparty proposes wording, they
    // don't name versions, re-lay-out the page, stamp it, or define its fields.
    allowSaveVersion: !isCounterparty,
    allowPageSetup: !isCounterparty,
    allowWatermark: !isCounterparty,
    allowInsertPlaceholder: !isCounterparty,
    allowImportNewVersion: !isCounterparty && !disableWordExport,
  };
};
